import { PlacementWithLocation } from "../../../../types";
import { GroupedTrip, LockSuggestion, TripParkingState } from "../types";

/**
 * Lock suggestion priority levels (higher = stronger recommendation)
 */
export const LOCK_PRIORITIES = {
  VIP: 10,
  MANUAL_PLACEMENT: 8,
  LONG_DWELL: 6,
  PREMIUM_ZONE: 5,
} as const;

/**
 * Threshold for "long dwell" trips (in hours)
 * 5 days = 120 hours
 */
export const LONG_DWELL_THRESHOLD_HOURS = 120;

/**
 * Threshold for "premium zone" priority
 */
export const PREMIUM_ZONE_THRESHOLD = 8;

/**
 * Calculates lock suggestions for trips based on various criteria.
 * Suggests locking trips that are:
 * - VIP tenants (highest priority)
 * - Manually placed (user intentionally placed them)
 * - Long dwell (5+ days)
 * - In premium zones (zone priority 8+)
 *
 * @returns Array of suggestions sorted by priority (highest first)
 */
export function calculateLockSuggestions(
  groupedTrips: GroupedTrip[],
  tripStates: Map<string, TripParkingState>,
  placements: PlacementWithLocation[]
): LockSuggestion[] {
  const suggestions: LockSuggestion[] = [];

  groupedTrips.forEach((trip) => {
    // Skip if already locked
    const tripState = tripStates.get(trip.trip_id) || "unlocked";
    if (tripState === "locked") return;

    // Calculate dwell hours
    const startTime = new Date(trip.start_time);
    const endTime = new Date(trip.end_time);
    const dwellHours =
      (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

    // Find corresponding placement to check additional properties
    const tripPlacement = placements.find((p) => p.trip_id === trip.trip_id);

    // Suggestion 1: VIP tenant
    if (trip.is_vip) {
      suggestions.push({
        trip_id: trip.trip_id,
        tail_number: trip.tail_number,
        reason: "VIP tenant",
        priority: LOCK_PRIORITIES.VIP,
      });
      return; // Only one suggestion per trip
    }

    // Suggestion 2: Manual placement (user intentionally placed it)
    if (
      trip.placement_source === "manual" ||
      tripPlacement?.placement_source === "manual"
    ) {
      suggestions.push({
        trip_id: trip.trip_id,
        tail_number: trip.tail_number,
        reason: "Manual placement",
        priority: LOCK_PRIORITIES.MANUAL_PLACEMENT,
      });
      return;
    }

    // Suggestion 3: Long dwell (5+ days)
    if (dwellHours >= LONG_DWELL_THRESHOLD_HOURS) {
      suggestions.push({
        trip_id: trip.trip_id,
        tail_number: trip.tail_number,
        reason: `Long dwell (${Math.round(dwellHours / 24)} days)`,
        priority: LOCK_PRIORITIES.LONG_DWELL,
      });
      return;
    }

    // Suggestion 4: Premium zone (priority 8+)
    if (trip.zone_priority && trip.zone_priority >= PREMIUM_ZONE_THRESHOLD) {
      suggestions.push({
        trip_id: trip.trip_id,
        tail_number: trip.tail_number,
        reason: "Premium zone",
        priority: LOCK_PRIORITIES.PREMIUM_ZONE,
      });
      return;
    }
  });

  // Sort by priority (highest first)
  return suggestions.sort((a, b) => b.priority - a.priority);
}
